import axios from 'axios';
import { getUserData } from './storageService';

// Use same base URL pattern as other services
const BASE_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com';
// Remove trailing /api if exists to avoid duplication
const API_URL = BASE_URL.replace(/\/api$/, '');

// Create axios instance with auth header
const getAuthHeaders = () => {
  const user = getUserData();
  return {
    Authorization: `Bearer ${user?.userId || ''}`
  };
};

const api = axios.create({
  baseURL: `${API_URL}/api/tasks`,
});

// Add auth header to all requests
api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...getAuthHeaders() };
  return config;
});

/**
 * Task Service - API calls for task management
 */
const taskService = {
  /**
   * Get all tasks with filters
   */
  getTasks: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    
    const response = await api.get(`/?${params.toString()}`);
    return response.data;
  },

  /**
   * Get tasks for Kanban board (grouped by status)
   */
  getKanbanTasks: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    
    const response = await api.get(`/kanban?${params.toString()}`);
    return response.data;
  },

  /**
   * Get task statistics
   */
  getStats: async () => {
    const response = await api.get('/stats');
    return response.data;
  },

  /**
   * Get single task by ID
   */
  getTask: async (id) => {
    const response = await api.get(`/${id}`);
    return response.data;
  },

  /**
   * Create new task
   */
  createTask: async (taskData) => {
    const response = await api.post('/', taskData);
    return response.data;
  },

  /**
   * Update task
   */
  updateTask: async (id, taskData) => {
    const response = await api.put(`/${id}`, taskData);
    return response.data;
  },

  /**
   * Update task status (for Kanban drag)
   */
  updateTaskStatus: async (id, status, order) => {
    const response = await api.put(`/${id}/status`, { status, order });
    return response.data;
  },

  /**
   * Reorder tasks (bulk update)
   */
  reorderTasks: async (tasks) => {
    const response = await api.put('/reorder/bulk', { tasks });
    return response.data;
  },

  /**
   * Delete task
   */
  deleteTask: async (id) => {
    const response = await api.delete(`/${id}`);
    return response.data;
  },

  /**
   * Add comment to task
   */
  addComment: async (taskId, content) => {
    const response = await api.post(`/${taskId}/comments`, { content });
    return response.data;
  },

  /**
   * Get performance report
   */
  getReport: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    
    const response = await api.get(`/report/performance?${params.toString()}`);
    return response.data;
  },

  /**
   * Get marketing team users (for assignee dropdown)
   */
  getMarketingUsers: async () => {
    const response = await api.get('/users/marketing');
    return response.data;
  }
};

export default taskService;
